import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Bot, X, Send, Mic, MicOff, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = '/api';

export const SemmyWidget = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  const sendMessage = useCallback(async (messageText) => {
    if (!messageText.trim() || loading) return;

    setInput('');
    setInterimTranscript('');
    setMessages(prev => [...prev, { role: 'user', content: messageText }]);
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/ai/chat`,
        { message: messageText },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        provider: response.data.provider
      }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: error.response?.data?.detail || 'Eroare la comunicarea cu AI',
        isError: true
      }]);
    } finally {
      setLoading(false);
    }
  }, [loading]);

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      setSpeechSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'ro-RO';
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        console.log('Speech recognition started');
        setIsListening(true);
      };

      recognition.onresult = (event) => {
        let interim = '';
        let final = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            final += transcript;
          } else {
            interim += transcript;
          }
        }
        
        if (interim) {
          setInterimTranscript(interim);
        }
        
        if (final) {
          setInterimTranscript('');
          sendMessage(final.trim());
        }
      };

      recognition.onend = () => {
        console.log('Speech recognition ended');
        setIsListening(false);
        setInterimTranscript('');
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        setInterimTranscript('');
        
        if (event.error === 'not-allowed') {
          alert('Microfonul nu este permis. Te rog acordă permisiunea în setările browserului.');
        } else if (event.error === 'no-speech') {
          // Silently ignore - user didn't speak
        } else {
          console.log('Speech error:', event.error);
        }
      };

      recognitionRef.current = recognition;
    } else {
      setSpeechSupported(false);
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [sendMessage]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const toggleListening = async () => {
    if (!recognitionRef.current) {
      alert('Browserul tău nu suportă recunoașterea vocală. Folosește Chrome, Edge sau Safari.');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      return;
    }

    // Request microphone permission
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      recognitionRef.current.start();
    } catch (err) {
      console.error('Microphone error:', err);
      if (err.name === 'NotAllowedError') {
        alert('Te rog acordă permisiunea pentru microfon în browser.');
      } else {
        alert('Nu s-a putut accesa microfonul: ' + err.message);
      }
    }
  };

  const handleSend = () => {
    sendMessage(input);
  };

  // Don't render if user is not logged in
  if (!user) return null;

  return (
    <>
      {/* Chat Widget Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-20 right-4 z-[9999] flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-3 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105"
          data-testid="semmy-widget-btn"
        >
          <Bot className="w-5 h-5" />
          <span className="font-medium">Semmy</span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-20 right-4 z-[9999] w-[380px] h-[500px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot className="w-6 h-6" />
              <div>
                <h3 className="font-semibold">Semmy</h3>
                <p className="text-xs opacity-80">Asistentul tău AI</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-auto p-4 space-y-3 bg-gray-50">
            {messages.length === 0 && (
              <div className="text-center text-gray-500 mt-8">
                <Bot className="w-12 h-12 mx-auto mb-3 text-orange-300" />
                <p className="font-medium">Bună! Sunt Semmy.</p>
                <p className="text-sm mt-1">Cu ce te pot ajuta azi?</p>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                    msg.role === 'user'
                      ? 'bg-orange-500 text-white rounded-br-md'
                      : msg.isError
                      ? 'bg-red-100 text-red-800 rounded-bl-md'
                      : 'bg-white text-gray-800 shadow-sm rounded-bl-md'
                  }`}
                >
                  {msg.role === 'assistant' && msg.provider && (
                    <Badge variant="outline" className="mb-1 text-[10px] px-1 py-0">
                      {msg.provider === 'gemini' ? 'Gemini' : 'Claude'}
                    </Badge>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                  <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t bg-white">
            {isListening && (
              <div className="mb-2 flex items-center justify-center gap-2 text-red-500 text-sm animate-pulse">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-ping" />
                <span>Ascult... vorbește acum</span>
              </div>
            )}
            {interimTranscript && (
              <div className="mb-2 text-gray-400 text-sm italic px-2">
                "{interimTranscript}"
              </div>
            )}
            <div className="flex gap-2 items-end">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={isListening ? "Ascult..." : "Scrie un mesaj..."}
                className="min-h-[44px] max-h-[100px] resize-none text-sm rounded-xl"
                disabled={isListening}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
              />
              {speechSupported && (
                <Button
                  size="icon"
                  variant={isListening ? "destructive" : "outline"}
                  onClick={toggleListening}
                  disabled={loading}
                  className={`shrink-0 rounded-xl ${isListening ? 'animate-pulse ring-2 ring-red-500' : ''}`}
                  title={isListening ? "Ascult... Click pentru a opri" : "Apasă pentru a vorbi cu Semmy"}
                >
                  {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </Button>
              )}
              <Button
                size="icon"
                onClick={handleSend}
                disabled={loading || !input.trim()}
                className="shrink-0 rounded-xl bg-orange-500 hover:bg-orange-600"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
